import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject
} from "@angular/core";

import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  NbCardModule,
  NbInputModule,
  NbSpinnerModule,
  NbSelectModule,
  NbOptionModule,
  NbCheckboxModule,
  NbIconModule,
  NbDialogService
} from "@nebular/theme";
import { BasicDialogComponent } from "../../basic-dialog/basic-dialog.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { KPI, APIService } from "../../../../@core/api";
import {
  AssetsService,
  StateLevelsService,
  ScopeService,
  ConfigService,
  NotationsService,
  ConsistencyService,
  AuditsService
} from "../../../../@core/utils";
import { ConsistencyLifeTimeWithNote } from "../../../../@core/utils/consistency.service";
import { LEVELS_MAP, STATE_LIMITS } from "../../../../@core/utils/constants";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { first, Subscription, Observable, combineLatest } from "rxjs";
import { map } from "rxjs/operators";

@Component({
  selector: "app-summary-tab",
  templateUrl: "./summary-tab.component.html",
  styleUrls: ["./summary-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbCheckboxModule,
    NbIconModule,
    FontAwesomeModule,
    TranslateModule
  ]
})
export class SummaryTabComponent implements OnChanges, OnInit, OnDestroy {
  // interactions
  @Input() public asset: KPI.MappedAsset;
  @Input() public loading: boolean = false;
  @Input() public maintenanceManagementEnabled: boolean = false;
  @Input() public equipmentValidationEnabled: boolean = false;
  @Output() public formChange = new EventEmitter<{ form: UntypedFormGroup; initialValues: any }>();
  @Output() public assetDataChanged = new EventEmitter<void>();
  @Output() public technicalStateChanged = new EventEmitter<any>();
  @Output() public validationChanged = new EventEmitter<boolean>();
  @Output() public remainingLifetimeChanged = new EventEmitter<any>();
  @Output() public confirmationModalStateChanged = new EventEmitter<boolean>();
  @Output() public requestSave = new EventEmitter<void>();

  // dependencies
  private assetsService = inject(AssetsService);
  private stateLevelService = inject(StateLevelsService);
  private scopeService = inject(ScopeService);
  private cdr = inject(ChangeDetectorRef);
  private configService = inject(ConfigService);
  private datePipe = inject(DatePipe);
  private translate = inject(TranslateService);
  private notationsService = inject(NotationsService);
  private consistencyService = inject(ConsistencyService);
  private apiService = inject(APIService);
  private auditService = inject(AuditsService);
  private dialogService = inject(NbDialogService);

  public form: UntypedFormGroup;
  public initialValues: any;
  public technicalStateLabel: string = "";
  public technicalStatusEvaluatedBy: string = "";
  public technicalStatusEvaluationDate: string = "";
  public userCorporate: string = "";
  public iniesDataSheets: string[] = [];
  public showIniesModal: boolean = false;
  public showIniesDataSheet: boolean = false;
  public maintenanceEvaluation: string = "";
  public maintenanceEvaluatedBy: string = "";
  public maintenanceEvaluationDate: string = "";
  public maintenanceCorporate: string = "";
  public missingNotationReasons: Array<{ value: number; name: string }> = [];
  private notationMissingComments: Map<number, string> = new Map();
  public remainingYearsSource: "modèle" | "auditor" = "modèle";
  public isRemainingYearsManuallyEdited: boolean = false;
  private isRecalculatingRemainingYears: boolean = false;
  public currentNote: number | null = null;
  public currentYear: number = new Date().getFullYear();
  public plannedEndOfLifeYear: number | null = null;
  public plannedLifetimeYears: number | null = null;
  public technicalStateOptions: Array<{ value: number; label: string; note: number | null }> = [];
  private alphaCoefficient: number = 2;
  public ratingReasons: KPI.RatingReason[] = [];
  public ratingReasonsForState: KPI.RatingReason[] = [];
  public selectedRatingReasonIds: number[] = [];
  public updatingRatingReasons: boolean = false;
  private currentStateId: number | null = null;
  private notationStates: Array<{ id: number; low: number; high: number }> = this.auditService.notationStates();
  public suggestedLifetime: ConsistencyLifeTimeWithNote | null = null;
  public dangerClass: string = "";
  private modelSuggestion: number | null = null;
  public isTechnicalStateValid: boolean = true;
  private readonly STATE_LIMITS = STATE_LIMITS;
  private readonly GOOD_STATE_EXPECTED_REASON_COUNT = 2;
  private subscriptions: Subscription[] = [];
  private formSubscriptions: Subscription[] = [];
  private userChoseYesForRecalculate: boolean = false;
  @ViewChild('motifAbsenceEvaluationSelect', { read: ElementRef }) motifAbsenceEvaluationSelect: ElementRef;
  @ViewChild('motifEvaluationSelect', { read: ElementRef }) motifEvaluationSelect: ElementRef;

  ngOnInit(): void {
    this.loadMissingNotationReasons();
    this.loadNotationMissingComments();
    this.loadTechnicalStateOptions();
    this.loadAlphaCoefficient();
    this.loadRatingReasons();

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.asset) {
          this.loadTechnicalStatusEvaluation();
          this.loadMissingNotationReasons();
          this.loadNotationMissingComments();
          this.loadTechnicalStateOptions();
        }
      })
    );
  }

  private loadAlphaCoefficient(): void {
    this.subscriptions.push(
      this.configService.customerConfig$.pipe(first()).subscribe(
        config => {
          this.alphaCoefficient =
            (this.asset?.asset_type_object as any)?.alpha_coefficient || (config as any)?.asset_age_factor || 2;
        },
        () => {
          this.alphaCoefficient = 2;
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.formSubscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset) {
      this.initializeForm();
      this.loadTechnicalState();
      this.loadTechnicalStatusEvaluation();
      this.loadAssetTypeDetails();
      this.loadConfiguration();
      this.loadMaintenanceEvaluation();
      this.cdr.detectChanges();
    }
  }

  public loadTechnicalState(): void {
    if (this.technicalStateOptions.length === 0) {
      this.loadTechnicalStateOptions();
    }
    this.subscriptions.push(
      combineLatest([
        this.assetsService.getAssetLifecycle(this.asset.id),
        this.scopeService.ratingReasons$.pipe(
          first(),
          map(reasonsMap => Object.values(reasonsMap))
        ),
      ]).subscribe(([lifecycle, ratingReasons]) => {
        this.ratingReasons = ratingReasons.sort((a, b) => a.ordering - b.ordering);
        this.currentYear = new Date().getFullYear();
        this.plannedEndOfLifeYear = lifecycle.planned_eol;
        this.plannedLifetimeYears = lifecycle.planned_eol - lifecycle.installation_year;
        const lastAuditPoint =
          lifecycle.audit_points && lifecycle.audit_points.length > 0
            ? lifecycle.audit_points[lifecycle.audit_points.length - 1]
            : null;
        const hasRealAudit = lastAuditPoint !== null,
          notes = lifecycle?.audit_points,
          latestNotes = notes.length ? notes[notes.length - 1] : null;
        const note = hasRealAudit ? latestNotes?.y : null;
        const missingReason = (this.asset as any).notation_missing_reason;
        const hasMissingReason =
          missingReason !== null && missingReason !== undefined && missingReason !== KPI.MissingNotationReason.NOT_SET;
        if (note === null || note === undefined || hasMissingReason) {
          this.currentNote = null;
          const noNotationLabel = this.translate.instant("core.utils.state-levels.no_notation");
          this.technicalStateLabel = noNotationLabel;
          const missingComment = (this.asset as any).notation_missing_comment || "";
          this.technicalStatusEvaluatedBy = "";
          this.technicalStatusEvaluationDate = "";
          this.currentStateId = null;
          this.ratingReasonsForState = [];
          this.selectedRatingReasonIds = [];
          if (this.form) {
            this.form.patchValue({
              etat_technique: "-",
              motif_evaluation: [],
              nb_annees_restantes: 0,
              motif_absence_evaluation: missingReason || KPI.MissingNotationReason.WAITING_EVALUATION,
              notation_missing_comment: missingComment
            });
          }
          this.initialValues.etat_technique = "-";
          this.initialValues.motif_evaluation = [];
          this.initialValues.nb_annees_restantes = 0;
          this.initialValues.motif_absence_evaluation = missingReason || KPI.MissingNotationReason.WAITING_EVALUATION;
          this.initialValues.notation_missing_comment = missingComment;
        } else {
          this.currentNote = note;
          const matchingOption = this.technicalStateOptions.find(opt =>
            opt.note === this.stateLevelService.getTechnicalStateOptionNoteFromNotation(note)
          );
          this.technicalStateLabel = matchingOption
            ? matchingOption.label
            : this.stateLevelService.getTechnicalStateLabelFromNote(note);
          this.currentStateId = this.getStateIdFromNote(note);
          const existingRatingReasonIds = lastAuditPoint?.rating_reasons?.map((r: any) => r.id) || [];
          if (this.currentStateId !== null) {
            this.selectedRatingReasonIds = existingRatingReasonIds;
            this.getRatingReasonsByState(this.currentStateId, true);
          } else {
            this.ratingReasonsForState = [];
            this.selectedRatingReasonIds = existingRatingReasonIds;
          }
          const currentYear = new Date().getFullYear();
          const remainingYears = lifecycle.planned_eol - currentYear;
          if (lastAuditPoint) {
            const evaluatedBy = lastAuditPoint.user?.name || "";
            this.technicalStatusEvaluatedBy = this.formatEvaluatedByName(evaluatedBy);
            const rawDate = lastAuditPoint.datetime || "";
            this.technicalStatusEvaluationDate = this.formatDate(rawDate);
            this.userCorporate = (this.asset as any).user_corporate || "";
          } else {
            this.loadTechnicalStatusEvaluation();
          }
          this.verifyDataConsistency(note, remainingYears, lifecycle);
          if (this.form) {
            this.form.patchValue({
              etat_technique: this.technicalStateLabel,
              motif_evaluation: this.selectedRatingReasonIds,
              nb_annees_restantes: remainingYears
            });
          }
          this.initialValues.etat_technique = this.technicalStateLabel;
          this.initialValues.motif_evaluation = this.selectedRatingReasonIds;
          this.initialValues.nb_annees_restantes = remainingYears;
          this.checkTechnicalStateValidation();
        }
        this.formChange.emit({ form: this.form, initialValues: this.initialValues });
        this.assetDataChanged.emit();
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      })
    );
  }

  private loadMissingNotationReasons(): void {
    const missingNotationOrder = [5, 7, 8, 2, 1, 3, 4, 6];
    this.missingNotationReasons = missingNotationOrder
      .filter(index => index >= 0 && index < KPI.MissingNotationReason.MAX_VALUE)
      .map(index => {
        const translationKey = this.notationsService.getMissingNotationReasonLabel(index as KPI.MissingNotationReason);
        const translatedName = this.translate.instant(translationKey);
        return {
          value: index,
          name: translatedName
        };
      });
  }

  private loadNotationMissingComments(): void {
    const commentsMap: Map<number, string> = new Map();
    const missingNotationOrder = [5, 7, 8, 2, 1, 3, 4, 6];
    missingNotationOrder.forEach(index => {
      const translationKey = this.notationsService.getMissingNotationReasonLabel(index as KPI.MissingNotationReason);
      const defaultComment = this.translate.instant(translationKey);
      commentsMap.set(index, defaultComment);
    });

    this.notationMissingComments = commentsMap;
  }

  private getStateIdFromNote(note: number): number | null {
    if (note === null || note === undefined) {
      return null;
    }

    if (this.notationStates && this.notationStates.length > 0) {
      let stateId: number | undefined;
      this.notationStates.forEach(state => {
        if (state.high !== 100) {
          if (note >= state.low && note < state.high) {
            stateId = state.id;
          }
        } else {
          if (note > state.low && note <= state.high) {
            stateId = state.id;
          }
        }
      });

      if (stateId !== undefined) {
        return stateId;
      }
    }

    if (!this.ratingReasons || this.ratingReasons.length === 0) {
      return null;
    }

    const allStateIds = new Set<number>();
    this.ratingReasons.forEach(reason => {
      if (reason.only_for_states && Array.isArray(reason.only_for_states)) {
        reason.only_for_states.forEach(id => allStateIds.add(id));
      }
    });

    const stateIdsArray = Array.from(allStateIds).sort((a, b) => a - b);
    if (stateIdsArray.length === 0) {
      return null;
    }

    const getReasonsForState = (stateId: number): KPI.RatingReason[] => {
      return this.ratingReasons.filter(r => r.only_for_states && r.only_for_states.includes(stateId));
    };

    const hasNormalLifecycle = (stateId: number): boolean => {
      const reasons = getReasonsForState(stateId);
      return reasons.some(r => r.name && r.name.toLowerCase().includes("normal lifecycle"));
    };

    const hasCriticalReasons = (stateId: number): boolean => {
      const reasons = getReasonsForState(stateId);
      const criticalKeywords = ["personal safety", "accident", "recurrent breakdown", "partially degraded"];
      return reasons.some(r => {
        const name = (r.name || "").toLowerCase();
        return criticalKeywords.some(keyword => name.includes(keyword));
      });
    };

    let stateId: number | null = null;
    if (note === this.STATE_LIMITS[0]) {
      const deadStateIds = stateIdsArray.filter(id => {
        const reasons = getReasonsForState(id);
        const hasDeadReasons = reasons.some(r => {
          const name = (r.name || "").toLowerCase();
          return name.includes("broken") || name.includes("accident") || name.includes("out of service");
        });
        return hasDeadReasons && !hasNormalLifecycle(id);
      });
      stateId = deadStateIds.length > 0 ? deadStateIds[0] : stateIdsArray[0];
    } else if (note > this.STATE_LIMITS[0] && note <= this.STATE_LIMITS[1]) {
      const criticalStateIds = stateIdsArray.filter(id => {
        return !hasNormalLifecycle(id) && hasCriticalReasons(id);
      });

      if (criticalStateIds.length === 0) {
        const noNormalLifecycleIds = stateIdsArray.filter(id => !hasNormalLifecycle(id));
        stateId = noNormalLifecycleIds.length > 0 ? noNormalLifecycleIds[0] : stateIdsArray[0];
      } else {
        stateId = criticalStateIds[0];
      }
    } else if (note > this.STATE_LIMITS[1] && note <= this.STATE_LIMITS[2]) {
      const badStateIds = stateIdsArray.filter(id => {
        const reasons = getReasonsForState(id);
        const hasBadReasons = reasons.some(r => {
          const name = (r.name || "").toLowerCase();
          return (
            name.includes("recurrent breakdown") ||
            name.includes("partially degraded") ||
            name.includes("maintenance issue") ||
            name.includes("abnormally excessive")
          );
        });
        return hasNormalLifecycle(id) || hasBadReasons;
      });
      stateId = badStateIds.length > 0 ? badStateIds[0] : stateIdsArray[0];
    } else if (note > this.STATE_LIMITS[2] && note <= this.STATE_LIMITS[3]) {
      const reasonsWithNormalLifecycleAsDefault = this.ratingReasons.filter(r => {
        const name = (r.name || "").toLowerCase();
        const hasNormalLifecycle = name.includes("normal lifecycle");
        if (!hasNormalLifecycle) return false;
        return r.default_for_states && Array.isArray(r.default_for_states) && r.default_for_states.length > 0;
      });

      const stateIdsWithNormalLifecycleDefault = new Set<number>();
      reasonsWithNormalLifecycleAsDefault.forEach(reason => {
        if (reason.default_for_states && Array.isArray(reason.default_for_states)) {
          reason.default_for_states.forEach(stateId => {
            if (reason.only_for_states && reason.only_for_states.includes(stateId)) {
              stateIdsWithNormalLifecycleDefault.add(stateId);
            }
          });
        }
      });

      const preferredStateIds = Array.from(stateIdsWithNormalLifecycleDefault).filter(id => {
        const reasons = getReasonsForState(id);
        return reasons.some(r => {
          const name = (r.name || "").toLowerCase();
          return name.includes("normal lifecycle");
        });
      });

      if (preferredStateIds.length > 0) {
        stateId = preferredStateIds.sort((a, b) => b - a)[0];
      } else {
        const acceptableStateIds = stateIdsArray.filter(id => {
          if (!hasNormalLifecycle(id)) {
            return false;
          }
          const reasons = getReasonsForState(id);
          const otherReasonsCount = reasons.filter(r => {
            const name = (r.name || "").toLowerCase();
            return !name.includes("normal lifecycle");
          }).length;

          return otherReasonsCount > 0;
        });

        if (acceptableStateIds.length > 0) {
          stateId = acceptableStateIds[0];
        } else {
          const fallbackStateIds = stateIdsArray.filter(id => hasNormalLifecycle(id));
          stateId = fallbackStateIds.length > 0 ? fallbackStateIds[0] : stateIdsArray[0];
        }
      }
    } else if (note > this.STATE_LIMITS[3] && note < this.STATE_LIMITS[5]) {
      const goodStateIds = stateIdsArray.filter(id => {
        if (!hasNormalLifecycle(id)) {
          return false;
        }
        const reasons = getReasonsForState(id);
        const totalReasonsCount = reasons.length;
        if (totalReasonsCount !== this.GOOD_STATE_EXPECTED_REASON_COUNT) {
          return false;
        }

        const hasNormalLifecycleReason = reasons.some(r => {
          const name = (r.name || "").toLowerCase();
          return name.includes("normal lifecycle");
        });

        return hasNormalLifecycleReason;
      });

      if (goodStateIds.length > 0) {
        stateId = goodStateIds[0];
      } else {
        const fallbackStateIds = stateIdsArray.filter(id => {
          if (!hasNormalLifecycle(id)) {
            return false;
          }
          const reasons = getReasonsForState(id);
          return reasons.length === this.GOOD_STATE_EXPECTED_REASON_COUNT;
        });
        if (fallbackStateIds.length > 0) {
          stateId = fallbackStateIds[0];
        } else {
          const fallbackStateIds2 = stateIdsArray
            .map(id => {
              if (!hasNormalLifecycle(id)) {
                return null;
              }
              const reasons = getReasonsForState(id);
              return { id, totalReasonsCount: reasons.length };
            })
            .filter(item => item !== null && item.totalReasonsCount >= this.GOOD_STATE_EXPECTED_REASON_COUNT)
            .sort((a, b) => a!.totalReasonsCount - b!.totalReasonsCount);
          stateId = fallbackStateIds2.length > 0 ? fallbackStateIds2[0]!.id : stateIdsArray[0];
        }
      }
    } else if (note >= this.STATE_LIMITS[4] && note <= this.STATE_LIMITS[5]) {
      const newStateIds = stateIdsArray.filter(id => hasNormalLifecycle(id));
      stateId = newStateIds.length > 0 ? newStateIds[newStateIds.length - 1] : stateIdsArray[stateIdsArray.length - 1];
    }
    return stateId;
  }

  private loadRatingReasons(): void {
    this.subscriptions.push(
      this.scopeService.ratingReasons$
        .pipe(
          first(),
          map(reasonsMap => Object.values(reasonsMap))
        )
        .subscribe(reasons => {
          this.ratingReasons = reasons.sort((a, b) => a.ordering - b.ordering);
          if (this.currentStateId !== null) {
            this.getRatingReasonsByState(this.currentStateId, true);
          }
        })
    );
  }

  private getRatingReasonsByState(stateId: number, init: boolean): void {
    if (!stateId) {
      this.ratingReasonsForState = [];
      this.selectedRatingReasonIds = [];
      return;
    }

    if (!this.ratingReasons || this.ratingReasons.length === 0) {
      return;
    }

    this.updatingRatingReasons = true;
    let tempSelectedReasonsIds = [...(this.selectedRatingReasonIds || [])];
    this.selectedRatingReasonIds = [];
    this.ratingReasonsForState = [];

    this.ratingReasons.forEach(reason => {
      const isAvailableForState = reason.only_for_states &&
        Array.isArray(reason.only_for_states) &&
        reason.only_for_states.some(onlyStateId => Number(onlyStateId) === Number(stateId));

      if (isAvailableForState) {
        this.ratingReasonsForState.push(reason);

        if (tempSelectedReasonsIds.length === 0) {
          const isDefaultForState = reason.default_for_states &&
            Array.isArray(reason.default_for_states) &&
            reason.default_for_states.some(defaultStateId => Number(defaultStateId) === Number(stateId));
          if (isDefaultForState) {
            tempSelectedReasonsIds.push(reason.id);
          }
        }
      } else if (!init && tempSelectedReasonsIds.includes(reason.id)) {
        tempSelectedReasonsIds.splice(tempSelectedReasonsIds.indexOf(reason.id), 1);
      }
    });

    this.selectedRatingReasonIds = tempSelectedReasonsIds;

    if (init) {
      setTimeout(() => {
        if (this.form) {
          this.form.patchValue({ motif_evaluation: this.selectedRatingReasonIds }, { emitEvent: false });
        }
        this.updatingRatingReasons = false;
        this.cdr.detectChanges();
      }, 0);
    } else {
      if (this.form) {
        this.form.patchValue(
          {
            motif_evaluation: this.selectedRatingReasonIds
          },
          { emitEvent: false }
        );
      }
      this.updatingRatingReasons = false;
      this.cdr.detectChanges();
      this.ratingReasonUpdated(this.selectedRatingReasonIds);
      if (this.selectedRatingReasonIds.length === 0) {
        setTimeout(() => {
          this.openMotifEvaluationDropdown();
          this.cdr.detectChanges();
        }, 50);
      }
    }
  }

  public ratingReasonUpdated(selectedReasons: number[]): void {
    if (!selectedReasons) {
      selectedReasons = [];
    }
    const previous = this.selectedRatingReasonIds || [];
    const changed =
      previous.length !== selectedReasons.length ||
      previous.some((id: number, index: number) => id !== selectedReasons[index]);

    if (changed) {
      this.selectedRatingReasonIds = selectedReasons;
      (this.asset as any).rating_reasons = selectedReasons;
      if (this.form) {
        this.form.patchValue(
          {
            motif_evaluation: selectedReasons
          },
          { emitEvent: true }
        );
      }
      this.checkTechnicalStateValidation();
      this.technicalStateChanged.emit({
        ratingReasons: selectedReasons,
        note: this.currentNote,
        level: this.getStateLevelForNote(this.currentNote),
        stateId: this.currentStateId
      });
      this.requestSave.emit();
      this.cdr.detectChanges();
    }
  }

  private checkTechnicalStateValidation(): void {
    let isValid = true;
    if (this.currentNote !== null && this.currentNote !== undefined) {
      const hasRatingReason = this.selectedRatingReasonIds && this.selectedRatingReasonIds.length > 0;
      if (!hasRatingReason) {
        isValid = false;
      }
    }

    if (this.currentNote === null || this.currentNote === undefined) {
      const missingReason = this.form?.get("motif_absence_evaluation")?.value;
      const hasMissingReason =
        missingReason !== null &&
        missingReason !== undefined &&
        missingReason !== KPI.MissingNotationReason.NOT_SET &&
        missingReason !== "" &&
        missingReason !== "-";
      if (!hasMissingReason) {
        isValid = false;
      }
    }

    this.isTechnicalStateValid = isValid;
    this.validationChanged.emit(isValid);
  }

  private loadTechnicalStateOptions(): void {

    const newLabel = this.translate.instant("core.utils.state-levels.new");
    const goodLabel = this.translate.instant("core.utils.state-levels.good");
    const acceptableLabel = this.translate.instant("core.utils.state-levels.acceptable");
    const badLabel = this.translate.instant("core.utils.state-levels.bad");
    const criticalLabel = this.translate.instant("core.utils.state-levels.critical");
    const outOfServiceLabel = this.translate.instant("core.utils.state-levels.out_of_service");
    const noNotationLabel = this.translate.instant("core.utils.state-levels.no_notation");
    // Each option maps to the highest note of its band (see STATE_LIMITS)
    this.technicalStateOptions = [
      { value: 5, label: newLabel, note: 100 },
      { value: 4, label: goodLabel, note: 94 },
      { value: 3, label: acceptableLabel, note: 70 },
      { value: 2, label: badLabel, note: 46 },
      { value: 1, label: criticalLabel, note: 22 },
      { value: 0, label: outOfServiceLabel, note: 0 },
      { value: -1, label: noNotationLabel, note: null }
    ];
  }

  public onTechnicalStateChange(selectedLabel: string): void {
    const noNotationLabel = this.translate.instant("core.utils.state-levels.no_notation");
    if (selectedLabel === "-") {
      selectedLabel = noNotationLabel;
    }

    const selectedOption = this.technicalStateOptions.find(opt => opt.label === selectedLabel);
    if (!selectedOption) {
      return;
    }

    if (selectedLabel === this.technicalStateLabel) {
      return;
    }

    const newNote = selectedOption.note;
    const previousStateId = this.currentStateId;

    if (newNote === null) {
      this.currentNote = null;
      this.technicalStateLabel = selectedLabel;
      this.currentStateId = null;
      this.ratingReasonsForState = [];
      this.selectedRatingReasonIds = [];
      this.technicalStatusEvaluatedBy = "";
      this.technicalStatusEvaluationDate = "";
      this.remainingYearsSource = "auditor";
      this.isRemainingYearsManuallyEdited = false;
      if (this.form) {
        const assetMissingReason = (this.asset as any).notation_missing_reason;
        const hasValidReason =
          assetMissingReason !== null &&
          assetMissingReason !== undefined &&
          assetMissingReason !== KPI.MissingNotationReason.NOT_SET;
        const missingReasonToSet = hasValidReason
          ? assetMissingReason
          : KPI.MissingNotationReason.NOT_SET;

        const noNotationLabel = this.translate.instant("core.utils.state-levels.no_notation");
        const displayValue = selectedLabel === noNotationLabel ? "-" : selectedLabel;

        this.form.patchValue({
          etat_technique: displayValue,
          motif_evaluation: [],
          nb_annees_restantes: 0,
          motif_absence_evaluation: missingReasonToSet,
          notation_missing_comment: ""
        });
      }

      this.checkTechnicalStateValidation();
      setTimeout(() => {
        this.cdr.detectChanges();
        this.openMotifAbsenceEvaluationDropdown();
      }, 100);
      return;
    }
    const newStateId = this.getStateIdFromNote(newNote);
    const remainingYears = this.recalculateLifetimeAfterNoteHasChanged(newNote);

    const previousNote = this.currentNote;
    this.currentNote = newNote;
    this.technicalStateLabel = selectedLabel;
    this.currentStateId = newStateId;
    this.isRecalculatingRemainingYears = true;
    this.remainingYearsSource = "modèle";
    this.isRemainingYearsManuallyEdited = false;
    this.userChoseYesForRecalculate = true;

    const isNewNoteCritical = newNote > this.STATE_LIMITS[0] && newNote <= this.STATE_LIMITS[1];
    const wasPreviousNoteCritical = previousNote !== null && previousNote > this.STATE_LIMITS[0] && previousNote <= this.STATE_LIMITS[1];
    const isChangingToCritical = isNewNoteCritical && !wasPreviousNoteCritical;

    if (newStateId !== null && newStateId !== previousStateId) {
      if (isChangingToCritical) {
        this.selectedRatingReasonIds = [];
        this.ratingReasonsForState = [];
        if (this.form) {
          this.form.patchValue({ motif_evaluation: [] }, { emitEvent: false });
        }

        setTimeout(() => {
          this.getRatingReasonsByState(newStateId, false);
        }, 0);
      } else {
        if (previousStateId === null || previousStateId === undefined) {
          this.selectedRatingReasonIds = [];
        }
        this.getRatingReasonsByState(newStateId, false);
      }
    } else if (newStateId === null) {
      this.ratingReasonsForState = [];
      this.selectedRatingReasonIds = [];
      if (this.form) {
        this.form.patchValue({ motif_evaluation: [] });
      }
    }

    this.form.patchValue({
      etat_technique: selectedLabel,
      nb_annees_restantes: remainingYears
    });

    const age = newNote === 100 ? 0 : this.currentYear - this.asset.installation_year;
    const theoreticalRemainingLifetime = this.asset.asset_type_object.expected_duration - age;
    let durationDeviation = remainingYears - theoreticalRemainingLifetime;
    if (theoreticalRemainingLifetime + durationDeviation < 0) {
      durationDeviation = -theoreticalRemainingLifetime;
    }
    (this.asset as any).duration_deviation = durationDeviation;

    this.verifyDataConsistency(newNote, remainingYears);
    setTimeout(() => {
      this.isRecalculatingRemainingYears = false;
    }, 0);

    setTimeout(() => {
      this.checkTechnicalStateValidation();
    }, 0);

    this.technicalStateChanged.emit({
      note: newNote,
      level: this.getStateLevelForNote(newNote),
      stateId: newStateId,
      ratingReasons: this.selectedRatingReasonIds,
      remainingYears: remainingYears
    });

    this.emitRemainingLifetimeChanged(remainingYears);

    this.requestSave.emit();
    this.cdr.detectChanges();
  }

  private noteValueToLevel(noteValue: number): number {
    let closestLevel = 1;
    let minDiff = Math.abs(LEVELS_MAP[1] - noteValue);
    for (let level = 2; level <= 14; level++) {
      const diff = Math.abs(LEVELS_MAP[level] - noteValue);
      if (diff < minDiff) {
        minDiff = diff;
        closestLevel = level;
      }
    }
    return closestLevel;
  }

  private getStateLevelForNote(note: number): number {
    if (note === null || note === undefined) {
      return 0;
    }

    const stateLevel = this.STATE_LIMITS.findIndex(limit => limit >= note);
    return stateLevel === -1 ? this.STATE_LIMITS.length - 1 : stateLevel;
  }

  private recalculateLifetimeAfterNoteHasChanged(note: number): number {
    if (!note && note !== 0) {
      return 0;
    }

    if (!this.asset?.installation_year || !this.asset?.asset_type_object?.expected_duration) {
      return 0;
    }

    let suggestedLifetime = this.calculateRemainingLifetimeFromModel(note);

    const remainingLifetimeDuration = Math.min(
      this.asset.asset_type_object.expected_duration,
      suggestedLifetime
    );
    const clampedRemainingLifetime = Math.max(0, remainingLifetimeDuration);
    this.modelSuggestion = clampedRemainingLifetime;

    return clampedRemainingLifetime;
  }

  public saveTechnicalStateAndReasons(): Observable<void> {
    return new Observable(observer => {
      if (!this.asset?.id || this.currentNote === null || this.currentNote === undefined) {
        observer.complete();
        return;
      }

      const level = this.noteValueToLevel(this.currentNote);
      const remainingYears = this.form?.get("nb_annees_restantes")?.value || 0;

      this.scopeService.auditQuestions$.pipe(
        first(),
        map(questions => Object.values(questions))
      ).subscribe(questions => {
        const technicalStateQuestion = questions.find(q => q.mnemonic === "technical_state");
        if (!technicalStateQuestion || !technicalStateQuestion.items || technicalStateQuestion.items.length === 0) {
          observer.complete();
          return;
        }

        const technicalStateItem =
          technicalStateQuestion.items.find(item => item.year_number === this.currentYear) ||
          technicalStateQuestion.items[0];

        const ratingReasonIds = this.selectedRatingReasonIds.length > 0 ? this.selectedRatingReasonIds : [];
        const auditNotation: KPI.AuditNotationPayload = {
          notes: [{
            question_item: technicalStateItem.id,
            note: level,
            mandatory: technicalStateQuestion.mandatory,
            rating_reasons: ratingReasonIds,
            comment: null
          }]
        };

        const currentYear = new Date().getFullYear();
        let age = 0;
        if (this.currentNote !== 100) {
          age = this.asset.installation_year ? currentYear - this.asset.installation_year : 0;
        }

        const expectedDuration = this.asset.asset_type_object?.expected_duration || 0;
        const durationDeviation = remainingYears + age - expectedDuration;

        const assetUpdatePayload: Partial<KPI.Asset> = {
          duration_deviation: durationDeviation,
          import_statuses: ["READY"] as any,
          notation_missing_reason: KPI.MissingNotationReason.NOT_SET,
          last_technical_evaluation: level
        } as any;

        this.apiService.assets_assets_patch(this.asset.id, assetUpdatePayload).subscribe({
          next: () => {
            const timestamp = Date.now();
            this.apiService.audit_notation_post(this.asset.id, auditNotation, timestamp).subscribe({
              next: () => {
                this.reloadAuditorAndDate();
                this.assetDataChanged.emit();
                observer.next();
                observer.complete();
              },
              error: error => {
                observer.error(error);
              }
            });
          },
          error: error => {
            observer.error(error);
          }
        });
      });
    });
  }

  public saveRatingReasons(): Observable<void> {
    return this.saveTechnicalStateAndReasons();
  }

  public saveMissingNotationReason(): Observable<void> {
    return new Observable(observer => {
      if (!this.asset?.id) {
        observer.complete();
        return;
      }

      const missingReason =
        this.form?.get("motif_absence_evaluation")?.value || KPI.MissingNotationReason.WAITING_EVALUATION;
      const missingComment = this.form?.get("notation_missing_comment")?.value || "";

      this.scopeService.auditQuestions$.pipe(
        first(),
        map(questions => Object.values(questions))
      ).subscribe(questions => {
        const technicalStateQuestion = questions.find(q => q.mnemonic === "technical_state");
        if (!technicalStateQuestion || !technicalStateQuestion.items || technicalStateQuestion.items.length === 0) {
          observer.complete();
          return;
        }

        const currentYear = new Date().getFullYear();
        const technicalStateItem =
          technicalStateQuestion.items.find(item => item.year_number === currentYear) ||
          technicalStateQuestion.items[0];

        const auditNotation: KPI.AuditNotationPayload = {
          notes: [{
            question_item: technicalStateItem.id,
            note: null,
            mandatory: technicalStateQuestion.mandatory,
            rating_reasons: [],
            comment: null
          }]
        };

        const assetUpdatePayload: any = {
          notation_missing_reason: missingReason,
          notation_missing_comment: missingComment,
          import_statuses: ["READY"] as any,
          last_technical_evaluation: null
        };

        this.apiService.assets_assets_patch(this.asset.id, assetUpdatePayload).subscribe({
          next: () => {
            const timestamp = Date.now();
            this.apiService.audit_notation_post(this.asset.id, auditNotation, timestamp).subscribe({
              next: () => {
                this.reloadAuditorAndDate();
                this.assetDataChanged.emit();
                observer.next();
                observer.complete();
              },
              error: error => {
                observer.error(error);
              }
            });
          },
          error: error => {
            observer.error(error);
          }
        });
      });
    });
  }

  private reloadAuditorAndDate(): void {
    if (!this.asset?.id) return;

    this.assetsService.getAssetLifecycle(this.asset.id).subscribe({
      next: (lifecycle) => {
        const lastAuditPoint =
          lifecycle.audit_points && lifecycle.audit_points.length > 0
            ? lifecycle.audit_points[lifecycle.audit_points.length - 1]
            : null;

        if (lastAuditPoint) {
          const evaluatedBy = lastAuditPoint.user?.name || "";
          this.technicalStatusEvaluatedBy = this.formatEvaluatedByName(evaluatedBy);
          const rawDate = lastAuditPoint.datetime || "";
          this.technicalStatusEvaluationDate = this.formatDate(rawDate);
          this.userCorporate = (this.asset as any).user_corporate || "";
          this.cdr.detectChanges();
        }
      }
    });
  }

  private loadTechnicalStatusEvaluation(): void {
    const evaluatedBy =
      (this.asset as any).technical_status_evaluated_by || (this.asset as any).technical_state_changed_by || "";
    this.technicalStatusEvaluatedBy = this.formatEvaluatedByName(evaluatedBy);
    const rawDate =
      (this.asset as any).technical_status_evaluation_date || (this.asset as any).technical_state_changed_on || "";
    this.technicalStatusEvaluationDate = this.formatDate(rawDate);
    this.userCorporate = (this.asset as any).user_corporate || "";
  }

  private formatEvaluatedByName(name: string): string {
    if (!name) return "";

    const trimmed = name.trim().replace(/\s+/g, " ");
    if (trimmed.match(/^[A-Za-z]\.\s/)) {
      return trimmed;
    }

    const parts = trimmed.split(/\s+/).filter(part => part.length > 0);
    if (parts.length === 0 || parts.length === 1) {
      return trimmed;
    }

    const firstLetter = parts[0].charAt(0).toUpperCase();
    const rest = parts.slice(1).join(" ");

    return `${firstLetter}. ${rest}`;
  }

  public getEvaluatedByText(): string {
    if (!this.technicalStatusEvaluatedBy && !this.technicalStatusEvaluationDate) return "";

    const parts: string[] = [];

    if (this.technicalStatusEvaluatedBy) {
      let evaluatedByText = this.technicalStatusEvaluatedBy;

      if (this.userCorporate) {
        evaluatedByText += ` (${this.userCorporate})`;
      }
      parts.push(evaluatedByText);
    }

    if (this.technicalStatusEvaluationDate) {
      parts.push(this.technicalStatusEvaluationDate);
    }

    if (parts.length === 0) return "";

    const evaluatedByLabel = this.translate.instant("asset_form_drawer.synthese.evaluated_by");
    const onDateLabel = this.translate.instant("asset_form_drawer.synthese.on_date");

    if (parts.length === 2) {
      return `${evaluatedByLabel} ${parts[0]} ${onDateLabel} ${parts[1]}`;
    } else if (this.technicalStatusEvaluatedBy) {
      return `${evaluatedByLabel} ${parts[0]}`;
    } else {
      return `${onDateLabel} ${parts[0]}`;
    }
  }

  public getSourceLabel(): string {
    return this.translate.instant("asset_form_drawer.synthese.source");
  }

  public getSourceValue(): string {
    return this.remainingYearsSource === "modèle"
      ? this.translate.instant("asset_form_drawer.synthese.source_model")
      : this.translate.instant("asset_form_drawer.synthese.source_auditor");
  }

  private verifyDataConsistency(
    note: number | null,
    currentRemainingYears: number,
    lifecycle?: KPI.AssetLifecycle
  ): void {
    this.suggestedLifetime = null;
    this.dangerClass = "";

    if (!note && note !== 0) {
      if (lifecycle?.consistency_check) {
        const isSourceModel = lifecycle.consistency_check.is_source_model !== false;
        this.remainingYearsSource = isSourceModel ? "modèle" : "auditor";
        this.isRemainingYearsManuallyEdited = !isSourceModel;
      } else {
        this.remainingYearsSource = "auditor";
        this.isRemainingYearsManuallyEdited = true;
      }
      return;
    }

    if (!this.asset?.installation_year || !this.asset?.asset_type_object?.expected_duration) {
      return;
    }

    const durationDeviation = (this.asset as any).duration_deviation || 0;
    const currentLifetime = this.asset.asset_type_object.expected_duration + durationDeviation;

    let age = this.currentYear - this.asset.installation_year;
    let computationNote = note;
    let inputLifetime = currentLifetime;

    if (note === 100) {
      inputLifetime = currentLifetime - age;
      age = 0;
      computationNote = note;
    }

    let calculateYear = this.currentYear;
    if ((this.asset as any).technical_state_changed_on) {
      const changedOn = (this.asset as any).technical_state_changed_on;
      if (typeof changedOn.getFullYear === "function") {
        calculateYear = changedOn.getFullYear();
      } else if (typeof changedOn === "string" || changedOn instanceof Date) {
        calculateYear = new Date(changedOn).getFullYear();
      }
    }

    const consistencyResult = this.consistencyService.areTechnicalStateAndLifetimeConsistent(
      calculateYear,
      age > 0 ? this.asset.installation_year : this.currentYear,
      computationNote,
      this.asset.asset_type_object.expected_duration,
      inputLifetime,
      this.alphaCoefficient
    );

    if (!consistencyResult.isConsistent) {
      this.suggestedLifetime = consistencyResult;
      this.dangerClass = "danger-class";
      this.validationChanged.emit(false);
    } else {
      this.suggestedLifetime = null;
      this.dangerClass = "";
      this.validationChanged.emit(true);
    }

    const remainingLifeTime =
      currentRemainingYears !== undefined && currentRemainingYears !== null
        ? currentRemainingYears
        : currentLifetime - (this.currentYear - this.asset.installation_year);

    const suggestedRemainingLifetime = this.consistencyService.getEstimatedRemainingLifetime(
      this.currentYear,
      age > 0 ? this.asset.installation_year : this.currentYear,
      computationNote,
      this.asset.asset_type_object.expected_duration,
      this.alphaCoefficient
    );

    this.modelSuggestion = suggestedRemainingLifetime;

    const clampedSuggestion = Math.min(
      this.asset.asset_type_object.expected_duration,
      Math.max(0, suggestedRemainingLifetime)
    );

    if (!this.userChoseYesForRecalculate && !this.isRecalculatingRemainingYears) {
      if (lifecycle?.consistency_check && lifecycle.consistency_check.is_source_model !== undefined) {
        const isSourceModel = lifecycle.consistency_check.is_source_model !== false;
        this.remainingYearsSource = isSourceModel ? "modèle" : "auditor";
        this.isRemainingYearsManuallyEdited = !isSourceModel;
      } else {
        const difference = Math.abs(remainingLifeTime - clampedSuggestion);
        if (difference <= 1) {
          this.remainingYearsSource = "modèle";
          this.isRemainingYearsManuallyEdited = false;
        } else {
          this.remainingYearsSource = "auditor";
          this.isRemainingYearsManuallyEdited = true;
        }
      }
    } else if (this.userChoseYesForRecalculate && lifecycle?.consistency_check?.is_source_model === true) {
      this.userChoseYesForRecalculate = false;
    }
  }

  private emitRemainingLifetimeChanged(remainingLifetime?: number): void {
    const lifetime =
      remainingLifetime !== undefined ? remainingLifetime : this.form?.get("nb_annees_restantes")?.value || 0;

    this.remainingLifetimeChanged.emit({
      remainingLifetime: lifetime,
      suggestedLifetime: this.modelSuggestion,
      source: this.remainingYearsSource
    });
  }

  public installationYearUpdated(): void {
    if (!this.form) {
      return;
    }

    const installationYear = this.form.get("installation_year")?.value;

    if (!installationYear) {
      return;
    }

    (this.asset as any).installation_year = installationYear;

    if (this.currentNote !== null && this.currentNote !== undefined && this.asset?.asset_type_object?.expected_duration) {
      const remainingYears = this.recalculateLifetimeAfterNoteHasChanged(this.currentNote);

      this.isRecalculatingRemainingYears = true;
      this.remainingYearsSource = "modèle";
      this.isRemainingYearsManuallyEdited = false;
      this.userChoseYesForRecalculate = true;

      this.form.patchValue(
        { nb_annees_restantes: remainingYears },
        { emitEvent: false }
      );

      this.verifyDataConsistency(this.currentNote, remainingYears);
      this.emitRemainingLifetimeChanged(remainingYears);

      setTimeout(() => {
        this.isRecalculatingRemainingYears = false;
      }, 0);

      this.technicalStateChanged.emit({
        installationYear: installationYear,
        remainingYears: remainingYears
      });

      this.requestSave.emit();
      this.cdr.detectChanges();
    } else {
      const currentRemainingYears = this.form.get("nb_annees_restantes")?.value || 0;
      if (currentRemainingYears > 0) {
        this.verifyDataConsistency(null, currentRemainingYears);
      }

      this.technicalStateChanged.emit({
        installationYear: installationYear,
        remainingYears: this.form.get("nb_annees_restantes")?.value
      });

      this.requestSave.emit();
      this.cdr.detectChanges();
    }
  }

  private calculateRemainingLifetimeFromModel(note: number): number {
    if (!note && note !== 0) {
      return 0;
    }

    if (!this.asset?.installation_year || !this.asset?.asset_type_object?.expected_duration) {
      return 0;
    }

    if (note === 100) {
      return this.asset.asset_type_object.expected_duration;
    }

    const suggestedLifetime = this.consistencyService.getEstimatedRemainingLifetime(
      this.currentYear,
      this.asset.installation_year,
      note,
      this.asset.asset_type_object.expected_duration,
      this.alphaCoefficient
    );

    return Math.min(this.asset.asset_type_object.expected_duration, Math.max(0, suggestedLifetime));
  }

  public remainingLifetimeUpdated(): void {
    if (!this.form) {
      return;
    }

    const remainingYears = this.form.get("nb_annees_restantes")?.value || 0;

    if (!this.asset?.installation_year || !this.asset?.asset_type_object?.expected_duration) {
      return;
    }

    const currentYear = new Date().getFullYear();
    const age = this.asset.installation_year ? currentYear - this.asset.installation_year : 0;
    const expectedDuration = this.asset.asset_type_object.expected_duration;
    const theoreticalRemainingLifetime = expectedDuration - age;
    const durationDeviation = remainingYears - theoreticalRemainingLifetime;

    if (theoreticalRemainingLifetime + durationDeviation < 0) {
      const minRemainingYears = 0;
      this.form.patchValue({ nb_annees_restantes: minRemainingYears }, { emitEvent: false });
      return;
    }

    if (remainingYears > expectedDuration) {
      this.form.patchValue(
        {
          nb_annees_restantes: expectedDuration
        },
        { emitEvent: false }
      );

      const clampedDurationDeviation = expectedDuration - theoreticalRemainingLifetime;
      (this.asset as any).duration_deviation = clampedDurationDeviation;

      if (this.currentNote !== null && this.currentNote !== undefined) {
        this.verifyDataConsistency(this.currentNote, expectedDuration);
      }
      this.emitRemainingLifetimeChanged(expectedDuration);
      this.cdr.detectChanges();
      return;
    }

    (this.asset as any).duration_deviation = durationDeviation;

    if (this.currentNote !== null && this.currentNote !== undefined) {
      this.verifyDataConsistency(this.currentNote, remainingYears);
    } else {
      this.remainingYearsSource = "auditor";
      this.isRemainingYearsManuallyEdited = true;
    }

    this.emitRemainingLifetimeChanged(remainingYears);

    this.cdr.detectChanges();
  }

  public onMissingNotationReasonChanged(): void {
    if (!this.form) {
      return;
    }

    const missingReason = this.form.get("motif_absence_evaluation")?.value;

    if (missingReason === null || missingReason === undefined || missingReason === KPI.MissingNotationReason.NOT_SET) {
      return;
    }

    (this.asset as any).notation_missing_reason = missingReason;

    const defaultComment = this.notationMissingComments.get(missingReason);

    if (defaultComment) {
      const now = new Date();
      const dateStr = this.datePipe.transform(now, "mediumDate", null, "fr-FR");
      const suffix = this.translate.instant("see email of {{date}}", { date: dateStr });

      const fullComment = `${defaultComment} - ${suffix}`;

      this.form.patchValue(
        {
          notation_missing_comment: fullComment
        },
        { emitEvent: false }
      );

      (this.asset as any).notation_missing_comment = fullComment;
    } else {
      this.form.patchValue(
        {
          notation_missing_comment: ""
        },
        { emitEvent: false }
      );

      (this.asset as any).notation_missing_comment = "";
    }

    this.checkTechnicalStateValidation();

    this.technicalStateChanged.emit({
      missingNotationReason: missingReason,
      missingNotationComment: (this.asset as any).notation_missing_comment,
      hasNotation: false
    });

    this.cdr.detectChanges();
  }

  public notationMissingCommentChanged(): void {
    if (!this.form) {
      return;
    }

    const comment = this.form.get("notation_missing_comment")?.value || "";

    (this.asset as any).notation_missing_comment = comment;

    this.technicalStateChanged.emit({
      missingNotationComment: comment
    });

    this.cdr.detectChanges();
  }

  private formatDate(dateString: string): string {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return this.datePipe.transform(date, "d MMM yy", undefined, this.translate.currentLang);
    } catch (error) {
      return dateString;
    }
  }

  private loadAssetTypeDetails(): void {
    this.subscriptions.push(
      this.scopeService.assetTypes$.pipe(first()).subscribe(assetTypes => {
        const assetTypeDetails = assetTypes[this.asset.asset_type];

        if (assetTypeDetails?.inies_environmental_data_sheet) {
          const iniesCount = assetTypeDetails.inies_environmental_data_sheet.length;
          this.iniesDataSheets = assetTypeDetails.inies_environmental_data_sheet;
          if (this.form?.get("fiche_iniies")) {
            this.form.get("fiche_iniies").setValue(iniesCount);
            this.initialValues.fiche_iniies = iniesCount;
            this.formChange.emit({ form: this.form, initialValues: this.initialValues });
          }
        }

        this.cdr.detectChanges();
        this.cdr.markForCheck();
      })
    );
  }

  private loadConfiguration(): void {
    this.subscriptions.push(
      this.configService.customerConfig$.pipe(first()).subscribe(config => {
        this.showIniesDataSheet = config?.inies_data_sheet_display_in_equipment_sheet || false;
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      })
    );
  }


  private initializeForm(): void {
    this.formSubscriptions.forEach(s => s.unsubscribe());
    this.formSubscriptions = [];

    this.initialValues = {
      installation_year: this.asset.installation_year || null,
      duree_vie_reference: this.getReferenceLifetime(),
      fiche_iniies: this.asset.asset_type_object?.inies_environmental_data_sheet?.length || 0,
      etat_technique: "",
      motif_evaluation: [],
      nb_annees_restantes: 0,
      motif_absence_evaluation: "",
      notation_missing_comment: "",
      maintenance_evaluation: (this.asset as any)?.maintenance?.equipment_evaluation || ""
    };

    this.form = new UntypedFormGroup({
      installation_year: new UntypedFormControl({ value: this.initialValues.installation_year, disabled: false }),
      duree_vie_reference: new UntypedFormControl({ value: this.initialValues.duree_vie_reference, disabled: false }),
      fiche_iniies: new UntypedFormControl({ value: this.initialValues.fiche_iniies, disabled: false }),
      etat_technique: new UntypedFormControl({
        value: this.initialValues.etat_technique === this.translate.instant("core.utils.state-levels.no_notation") ? "-" : this.initialValues.etat_technique,
        disabled: false
      }),
      motif_evaluation: new UntypedFormControl({ value: this.initialValues.motif_evaluation, disabled: false }),
      nb_annees_restantes: new UntypedFormControl({ value: this.initialValues.nb_annees_restantes, disabled: false }),
      motif_absence_evaluation: new UntypedFormControl({
        value: this.initialValues.motif_absence_evaluation,
        disabled: false
      }),
      notation_missing_comment: new UntypedFormControl(
        { value: this.initialValues.notation_missing_comment, disabled: false },
        [Validators.maxLength(512)]
      ),
      maintenance_evaluation: new UntypedFormControl({
        value: this.initialValues.maintenance_evaluation,
        disabled: false
      })
    });

    this.setupFormSubscriptions();
    this.formChange.emit({ form: this.form, initialValues: this.initialValues });
  }

  private setupFormSubscriptions(): void {
    this.formSubscriptions.forEach(s => s.unsubscribe());
    this.formSubscriptions = [];

    this.formSubscriptions.push(
      this.form.valueChanges.subscribe((v) => {
        console.log(v)
        this.formChange.emit({ form: this.form, initialValues: this.initialValues });
      }),
      this.form.get("nb_annees_restantes")?.valueChanges.subscribe(() => {
        if (this.isRecalculatingRemainingYears || this.isRemainingYearsManuallyEdited) {
          return;
        }
        const currentValue = this.form.get("nb_annees_restantes")?.value;
        const initialValue = this.initialValues.nb_annees_restantes;
        if (currentValue !== initialValue && currentValue !== null && currentValue !== undefined) {
          if (this.currentNote !== null && this.currentNote !== undefined) {
            this.verifyDataConsistency(this.currentNote, currentValue);
          } else {
            this.remainingYearsSource = "auditor";
            this.isRemainingYearsManuallyEdited = true;
          }
          this.cdr.detectChanges();
        }
      })
    );
  }


  public openIniesModal(): void {
    if (this.iniesDataSheets.length > 0) {
      this.showIniesModal = true;
    }
  }

  public closeIniesModal(): void {
    this.showIniesModal = false;
  }

  public openUrlInNewTab(url: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private openMotifEvaluationDropdown(): void {
    if (!this.motifEvaluationSelect?.nativeElement) {
      return;
    }

    const selectElement = this.motifEvaluationSelect.nativeElement;
    const button = selectElement.querySelector('button[type="button"]') ||
                   selectElement.querySelector('button') ||
                   selectElement.querySelector('.select-button') ||
                   selectElement.querySelector('[role="button"]');

    if (button) {
      (button as HTMLElement).click();
    } else {
      const nbSelectElement = selectElement.closest('nb-select');
      if (nbSelectElement) {
        (nbSelectElement as any).click?.();
      }
    }
  }

  private openMotifAbsenceEvaluationDropdown(): void {
    if (!this.motifAbsenceEvaluationSelect?.nativeElement) {
      return;
    }

    const selectElement = this.motifAbsenceEvaluationSelect.nativeElement;
    const button = selectElement.querySelector('button[type="button"]') ||
                   selectElement.querySelector('button') ||
                   selectElement.querySelector('.select-button') ||
                   selectElement.querySelector('[role="button"]');

    if (button) {
      (button as HTMLElement).click();
    } else {
      const nbSelectElement = selectElement.closest('nb-select');
      if (nbSelectElement) {
        (nbSelectElement as any).click?.();
      }
    }
  }

  /** Theoretical / reference lifetime for the asset type (in years). */
  public getReferenceLifetime(): number {
    return this.asset?.asset_type_object?.expected_duration || 0;
  }

  /**
   * Total expected lifetime for the equipment (in years) = reference lifetime + deviation.
   * Uses the live `duration_deviation` (updated as the user edits the remaining years) so the
   * value stays in sync before the asset's end_of_life is recomputed on save.
   */
  public getTotalExpectedLifetime(): number {
    const referenceLifetime = this.getReferenceLifetime();
    if (!referenceLifetime) {
      return 0;
    }
    const durationDeviation = (this.asset as any)?.duration_deviation || 0;
    return referenceLifetime + durationDeviation;
  }

  /** Warning shown when the total expected lifetime drops below 80% of the reference lifetime. */
  public isLifetimeBelowThreshold(): boolean {
    const referenceLifetime = this.getReferenceLifetime();
    const totalExpectedLifetime = this.getTotalExpectedLifetime();
    return (
      referenceLifetime > 0 &&
      totalExpectedLifetime > 0 &&
      totalExpectedLifetime < 0.8 * referenceLifetime
    );
  }

  public openLifetimeConsistencyModal(): void {
    this.dialogService.open(BasicDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: true,
      context: {
        canCancel: false,
        title: this.translate.instant("asset_form_drawer.synthese.consistency_check_title"),
        content: this.translate.instant("asset_form_drawer.synthese.lifetime_warning_total", {
          totalExpectedLifetime: this.getTotalExpectedLifetime()
        }),
        contentBis: this.translate.instant("asset_form_drawer.synthese.lifetime_warning_theoretical", {
          theoreticalLifetime: this.getReferenceLifetime()
        })
      }
    });
  }

  public loadMaintenanceEvaluation(): void {
    const maintenance = (this.asset as any).maintenance;
    this.maintenanceEvaluation = maintenance?.equipment_evaluation || "";
    this.maintenanceEvaluatedBy = this.formatEvaluatedByName(maintenance?.evaluated_by || "");
    this.maintenanceEvaluationDate = this.formatDate(maintenance?.evaluation_date || "");
    this.maintenanceCorporate = (this.asset as any).user_corporate || "";
  }

  public getMaintenanceEvaluatedByText(): string {
    if (!this.maintenanceEvaluatedBy && !this.maintenanceEvaluationDate) return "";

    const parts: string[] = [];

    if (this.maintenanceEvaluatedBy) {
      let evaluatedByText = this.maintenanceEvaluatedBy;
      if (this.maintenanceCorporate) {
        evaluatedByText += ` (${this.maintenanceCorporate})`;
      }
      parts.push(evaluatedByText);
    }

    if (this.maintenanceEvaluationDate) {
      parts.push(this.maintenanceEvaluationDate);
    }

    if (parts.length === 0) return "";

    const evaluatedByLabel = this.translate.instant("asset_form_drawer.synthese.evaluated_by");
    const onDateLabel = this.translate.instant("asset_form_drawer.synthese.on_date");

    if (parts.length === 2) {
      return `${evaluatedByLabel} ${parts[0]} ${onDateLabel} ${parts[1]}`;
    } else if (this.maintenanceEvaluatedBy) {
      return `${evaluatedByLabel} ${parts[0]}`;
    } else {
      return `${onDateLabel} ${parts[0]}`;
    }
  }

  public getMaintenanceEvaluationLabel(): string {
    if (!this.maintenanceEvaluation) return "-";
    switch (this.maintenanceEvaluation) {
      case "satisfactory":
        return this.translate.instant("asset_form_drawer.options.satisfactory");
      case "unsatisfactory":
        return this.translate.instant("asset_form_drawer.options.unsatisfactory");
      default:
        return this.maintenanceEvaluation;
    }
  }

  public isFieldMissing(fieldName: string): boolean {
    if (!this.equipmentValidationEnabled) {
      return false;
    }
    const value = this.form?.get(fieldName)?.value;
    let result = false;

    switch (fieldName) {
      case "etat_technique":
        const noRatingLabel = this.translate.instant("core.utils.state-levels.no_notation");
        const isNoRating = value === noRatingLabel || this.currentNote === null;
        result = !value || value === "" || isNoRating;
        break;
      default:
        result = false;
        break;
    }

    return result;
  }

  public onRecalculateRemainingYears(): void {
    if (!this.currentNote || !this.asset?.installation_year || !this.asset?.asset_type_object?.expected_duration) {
      return;
    }

    const installationYear = this.asset.installation_year;
    const expectedDuration = this.asset.asset_type_object.expected_duration;
    const alphaCoefficient = this.asset.asset_type_object.alpha_coefficient || 2;

    const proposedRemainingYears = this.consistencyService.getEstimatedRemainingLifetime(
      this.currentYear,
      installationYear,
      this.currentNote,
      expectedDuration,
      alphaCoefficient
    );

    this.isRecalculatingRemainingYears = true;
    this.remainingYearsSource = "modèle";
    this.isRemainingYearsManuallyEdited = false;
    this.userChoseYesForRecalculate = true;

    this.form.patchValue({
      nb_annees_restantes: proposedRemainingYears
    });

    setTimeout(() => {
      this.isRecalculatingRemainingYears = false;
    }, 0);

    this.cdr.detectChanges();
    this.requestSave.emit();
  }
}
